import Survey from './Survey';
import _groupBy from 'lodash/groupBy';
import _flattenDeep from 'lodash/flattenDeep';
import _uniqWith from 'lodash/uniqWith';
import { QuestionSearchItem, SearchItem } from './QuestionSearch';
import SurveyItem from './SurveyItem';
import { FhirSearchItem, PrimarySurveyItemIdentifier } from '.';
import { Version } from '../../../util/Versions';

/**
 * Utility class for organizing and working with a primary survey group
 */
export default class PrimarySurvey {
  /**
   * Given a collection of questionnaires (with and without primary survey IDs) returns a PrimarySurvey
   * collection each with corresponding questionnaires
   * @param allQuestionnaires
   */
  static initFromAllQuestionnaires(
    allQuestionnaires: fhir.Questionnaire[],
  ): PrimarySurvey[] {
    const asSurveys = (allQuestionnaires || []).map(_ => new Survey(_));

    // Group by primary survey ID.  Questionnaires without primary survey ID are in their own group of 1 questionnaire
    const withoutPrimarySurveyId = asSurveys.filter(_ => !_.hasPrimarySurveyId);
    const withPrimarySurveyIds = _groupBy(
      asSurveys.filter(_ => _.hasPrimarySurveyId),
      _ => _.primarySurveyId,
    );

    // return a PrimarySurvey for each group
    return [...withoutPrimarySurveyId, ...Object.values(withPrimarySurveyIds)]
      .map(_ => {
        const asQuestionnaires = (Array.isArray(_) ? _ : [_]).map(
          _ => _.questionnaire,
        );
        return new PrimarySurvey(asQuestionnaires);
      })
      .sort((a, b) => {
        const aIndex = asSurveys.findIndex(_ => _.id === a.surveys[0].id);
        const bIndex = asSurveys.findIndex(_ => _.id === b.surveys[0].id);

        return aIndex - bIndex;
      });
  }

  /**
   * Given a collection of questionnaires (with and without primary survey IDs) returns the primary survey which
   * contains the target survey
   * @param allQuestionnaires
   */
  static initFromAllQuestionnairesWithTargetSurvey(
    allQuestionnaires: fhir.Questionnaire[],
    surveyId: string,
  ): PrimarySurvey {
    const primarySurveys =
      PrimarySurvey.initFromAllQuestionnaires(allQuestionnaires);
    return (
      primarySurveys.find(_ => _.containsSurvey(surveyId)) ||
      new PrimarySurvey([])
    );
  }

  /**
   * Given a collection of questionnaires (with and without primary survey IDs) and a target primary survey ID
   * returns a PrimarySurvey for the applicable questionnaires of the provided Primary Survey ID
   * @param allQuestionnaires
   */
  static initFromTargetPrimarySurveyID(
    allQuestionnaires: fhir.Questionnaire[],
    primarySurvey: string,
  ): PrimarySurvey {
    if (!primarySurvey) {
      console.error(
        'Tried to initialize primary survey from a primary survey ID without supplying the ID',
      );
      return new PrimarySurvey([]);
    }

    const asSurveys = (allQuestionnaires || [])
      .filter(Boolean)
      .map(_ => new Survey(_));
    const questionnairesOfPrimarySurveyId = asSurveys
      .filter(_ => _.primarySurveyId === primarySurvey)
      .map(_ => _.questionnaire);

    if (!questionnairesOfPrimarySurveyId.length) {
      console.error(
        'Tried to initialize a primary survey, but no matching questionnaires found for the given primary survey ID',
      );
    }

    return new PrimarySurvey(questionnairesOfPrimarySurveyId);
  }

  private _primarySurveyId: string;
  private _surveys: Survey[];
  private _cachedSearchItems: QuestionSearchItem[];
  private _fhirSearchItems: FhirSearchItem[];

  constructor(primarySurveyQuestionnaires: fhir.Questionnaire[]) {
    const asSurveys = (primarySurveyQuestionnaires || [])
      .filter(Boolean)
      .map(_ => new Survey(_));

    const surveysWithoutPrimarySurveyId = asSurveys.filter(
      _ => !_.hasPrimarySurveyId,
    );

    if (surveysWithoutPrimarySurveyId.length > 1) {
      console.error(
        `Primary survey was initialized with multiple questionnaires without primary survey ID.  Only the first questionnaire will be used.`,
      );
    }

    const primarySurveyIdsInGroup = asSurveys
      .map(_ => _.primarySurveyId)
      .filter(Boolean);
    const primarySurveyIDs = [...new Set(primarySurveyIdsInGroup)];

    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    this._primarySurveyId = primarySurveyIDs[0];
    // if primary survey ID is not present on the given survey, they may just be a legacy survey and consider them valid
    let emptyPrimaryIDsAreValid = true;
    if (primarySurveyIDs.length > 1) {
      console.error(
        `Primary survey initialized with multiple primary survey IDs.  This is an invalid questionaire set and some questionnaires will be ommited from the given questionnaire set.  IDs: "${primarySurveyIDs.join(
          ', ',
        )}"`,
      );

      // this questionnaire collection is invalid and its not safe to assume that surveys without primary survey IDs are still valid
      emptyPrimaryIDsAreValid = false;
    }

    this._surveys = asSurveys.filter(_ => {
      if (_.hasPrimarySurveyId && _.primarySurveyId === this._primarySurveyId) {
        return true;
      } else if (
        !_.hasPrimarySurveyId &&
        emptyPrimaryIDsAreValid &&
        _.id === surveysWithoutPrimarySurveyId[0]?.id
      ) {
        return true;
      } else {
        return false;
      }
    });
  }

  get primarySurveyId(): string {
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    return this._primarySurveyId || null;
  }

  get hasPrimarySurveyId(): boolean {
    return !!this.primarySurveyId;
  }

  get surveys(): Survey[] {
    return this._surveys;
  }

  get questionnaires(): fhir.Questionnaire[] {
    return this._surveys.map(_ => _.questionnaire);
  }

  get questionnaireIds(): string[] {
    return this._surveys.map(_ => _.id).filter(Boolean);
  }

  /**
   * The surveys sorted by their version from earliest to latest versions
   */
  get sortedSurveys(): Survey[] {
    return this._surveys.sort((s1, s2) => s1.version.sortWith(s2.version));
  }

  /**
   * The latest survey based on its version
   */
  get latestSurvey(): Survey {
    return this.sortedSurveys[this.sortedSurveys.length - 1] || null;
  }

  /**
   * The latest survey based on its version and has one of the specified statuses
   */
  getLatestSurveyOfStatus(
    statuses: ('draft' | 'active' | 'retired')[],
  ): Survey {
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    return (
      [...this.sortedSurveys]
        .reverse()
        .find(_ => (statuses || []).includes(_?.status)) || null
    );
  }

  /**
   * Gets the survey items that are unique and not a verioned duplicate
   */
  private _getUniqueSurveyItems(): {
    surveyItem: SurveyItem;
    survey: Survey;
  }[] {
    const allControlSurveyItems = _flattenDeep(
      [...this.sortedSurveys].reverse().map(survey => {
        return survey.controlSurveyItems.map(surveyItem => ({
          survey,
          surveyItem,
        }));
      }),
    );

    return _uniqWith(allControlSurveyItems, (itemAndSurvey1, itemAndSurvey2) =>
      itemAndSurvey1.surveyItem.isVersionOfItem(itemAndSurvey2.surveyItem),
    );
  }

  /**
   * All the unique searchable question items for the primary survey
   * This dedupes verioned questions
   */
  get controlQuestionSearchItems(): QuestionSearchItem[] {
    if (!this._cachedSearchItems) {
      this._cachedSearchItems = this._getUniqueSurveyItems().map(_ =>
        new SearchItem(_.surveyItem, [], _.survey.id).toQuestionSearchItem(),
      );
    }

    return this._cachedSearchItems;
  }

  /**
   * All the unique fhir search items for the primary survey.
   * This dedupes verioned questions
   */
  get fhirSearchItems(): FhirSearchItem[] {
    if (!this._fhirSearchItems) {
      this._fhirSearchItems = this._getUniqueSurveyItems().map(
        _ => new FhirSearchItem(_.surveyItem, _.survey.id),
      );
    }

    return this._fhirSearchItems;
  }

  getTargetFhirSearchItem(
    primarySurveyItemIdentifier: PrimarySurveyItemIdentifier,
  ): FhirSearchItem {
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    return (
      this.fhirSearchItems.find(_ =>
        _.primarySurveyItemIdentifer.equals(primarySurveyItemIdentifier),
      ) || null
    );
  }

  getTargetSurveyItem(
    primarySurveyItemIdentifier: PrimarySurveyItemIdentifier,
  ): SurveyItem {
    const targetSurvey = (this._surveys || []).find(
      _ => _.id === primarySurveyItemIdentifier?.surveyId,
    );

    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    if (!targetSurvey) return null;

    return targetSurvey.getSurveyItemWithLinkId(
      primarySurveyItemIdentifier.linkId,
    );
  }

  get surveyIds(): string[] {
    return this._surveys.map(_ => _.id);
  }

  getTargetSurvey(surveyId: string | null): Survey {
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    return this._surveys.find(_ => _.id === surveyId);
  }

  getTargetSurveys(surveyIds: string[]): Survey[] {
    return this._surveys.filter(_ => surveyIds.includes(_.id));
  }

  containsSurvey(surveyId: string): boolean {
    return !!surveyId && !!this.getTargetSurvey(surveyId);
  }

  /**
   * Returns the sorted versions of the supplied survey IDs.  If no survey IDs are supplied, it
   * will return all versions
   */
  getVersions(surveyIds: string[] = this.surveyIds): Version[] {
    return this.sortedSurveys
      .filter(_ => surveyIds.includes(_?.id))
      .map(_ => _.version);
  }
}
